import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ApplicationFormNew.css';
import {
  renderFinancial,
  renderLifestyle,
  renderHousehold,
  renderHealthAccessibility,
  renderSafety,
  renderPreferences,
  renderUsefulItems,
  renderHouseholdCompatibility,
  renderReview
} from './ApplicationFormSteps';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationFormNew = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, access_code, savedData, isNew } = location.state || {};

  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [membershipResult, setMembershipResult] = useState(null);
  const [errors, setErrors] = useState({});
  const [lastSaved, setLastSaved] = useState(null);

  const totalSteps = 10;

  const [formData, setFormData] = useState({
    email: email || '',
    access_code: access_code || '',
    
    // Step 1: Personal Details
    shared_housing_type: 'single_female',
    first_name: '',
    last_name: '',
    preferred_name: '',
    age: '',
    gender: '',
    phone: '',
    city: '',
    state: 'NSW',
    postcode: '',
    
    // Step 2: Financial
    income_sources: [],
    maximum_weekly_rent: 0,
    
    // Step 3: Lifestyle
    has_pets: false,
    pet_details: '',
    daily_routine: [],
    hobbies: [],
    other_hobbies: '',
    
    // Step 4: Household & Community
    household_responsibilities: [],
    appreciate_help_with_tasks: false,
    happy_to_share_transport: false,
    cooking_preference: '',
    visitors_frequency: '',
    overnight_guests: '',
    religious_cultural_requirements: '',
    
    preferred_town_region: '',
    // Partner details (couples only)
    partner_first_name: '',
    partner_last_name: '',
    partner_preferred_name: '',
    partner_age: '',
    partner_gender: '',
    // Step 5: Health & Accessibility (NEW)
    mobility_status: '',
    medical_conditions: [],
    accessibility_requirements: [],
    other_accessibility_needs: '',

    // Step 6: Safety & References
    police_check_status: '',
    can_provide_references: true,
    dealbreakers: [],
    other_dealbreakers: '',
    things_i_hate: '',
    things_id_enjoy: '',
    hate_checkboxes: [],
    enjoy_checkboxes: [],
    other_hate: '',
    other_enjoy: '',
    
    // Step 6: Your Preferences
    age_range_preference: '',
    housemate_mix_preference: '',
    household_size_preference: '',
    accommodation_duration: '',
    deal_makers: '',
    deal_breakers: '',
    enjoyable_activity: '',
    non_negotiable_conditions: '',
    preferred_location: '',
    
    // Step 7: Useful Items
    useful_items: [],
    other_items: '',
    interested_member_support_role: '',
    // Step 8: Household Compatibility Questions
    hc_q1: '', hc_q2: '', hc_q3: '', hc_q4: '', hc_q5: '',
    hc_q6: '', hc_q7: '', hc_q8: '', hc_q9: '',
    
    // Metadata
    current_step: 1,
    last_updated: new Date().toISOString()
  });

  useEffect(() => {
    if (!email || !access_code) {
      navigate('/apply');
      return;
    }
    
    if (savedData) {
      setFormData(prev => ({ ...prev, ...savedData }));
      setCurrentStep(savedData.current_step || 1);
    }
  }, [email, access_code, savedData, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name.includes('[]')) {
        const fieldName = name.replace('[]', '');
        const currentValues = formData[fieldName] || [];
        setFormData(prev => ({
          ...prev,
          [fieldName]: checked 
            ? [...currentValues, value]
            : currentValues.filter(v => v !== value)
        }));
      } else {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const saveProgress = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/applications/save`, {
        ...formData,
        current_step: currentStep,
        last_updated: new Date().toISOString()
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save progress:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.first_name.trim()) newErrors.first_name = 'Given name is required';
      if (!formData.last_name.trim()) newErrors.last_name = 'Family name is required';
      // Phone: digits, spaces, hyphens only, at least 8 chars
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone is required';
      } else if (!/^[\d\s\-()+]+$/.test(formData.phone.trim())) {
        newErrors.phone = 'Phone must contain digits, spaces or hyphens only';
      } else if (formData.phone.replace(/\D/g, '').length < 8) {
        newErrors.phone = 'Phone number too short — at least 8 digits required';
      }
      // Age: number 50-99
      if (!formData.age) {
        newErrors.age = 'Age is required';
      } else if (isNaN(Number(formData.age)) || Number(formData.age) < 18 || Number(formData.age) > 120) {
        newErrors.age = 'Age must be a number between 18 and 120';
      }
      if (!formData.city.trim()) newErrors.city = 'City is required';
      // Postcode: exactly 4 digits
      if (!formData.postcode.trim()) {
        newErrors.postcode = 'Postcode is required';
      } else if (!/^\d{4}$/.test(formData.postcode.trim())) {
        newErrors.postcode = 'Postcode must be exactly 4 digits (e.g. 4000)';
      }
      // Gender vs. accommodation type mismatch (singles only — couples are mixed-gender by design)
      const g = formData.gender;
      const t = formData.shared_housing_type;
      if (g === 'male' && t === 'single_female') {
        newErrors.shared_housing_type = 'A male applicant cannot select a Single Female community. Please choose Single Male or Couples.';
      } else if (g === 'female' && t === 'single_male') {
        newErrors.shared_housing_type = 'A female applicant cannot select a Single Male community. Please choose Single Female or Couples.';
      }
    } else if (step === 2) {
      if (formData.income_sources.length === 0) newErrors.income_sources = 'Please select at least one income source';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      await saveProgress();
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/applications/submit`, {
        ...formData,
        status: 'pending',
        submitted_at: new Date().toISOString()
      });
      setSubmitSuccess(true);
      // Store membership type for confirmation display
      setMembershipResult(response.data);
      // GA4 conversion event — application submitted
      if (window.gtag) {
        window.gtag('event', 'application_submitted', {
          event_category: 'conversion',
          event_label: formData.shared_housing_type || 'unknown',
          value: 1
        });
      }
      window.scrollTo(0, 0);
    } catch (error) {
      console.error('Failed to submit application:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    const isGoldEnvoy = membershipResult?.membership_class === 'gold_envoy';
    return (
      <div className="application-form-new"><div className="container">
          <div className="success-container" data-testid="success-message">
            <div className="success-icon">✓</div>
            <h1>Application Submitted Successfully!</h1>
            <p>
              Thank you for your application, {formData.first_name}. We have received your submission 
              and will review it shortly. You will receive an email at <strong>{formData.email}</strong> 
              with updates on your application status.
            </p>

            {/* Membership type confirmation */}
            <div style={{ margin: '24px auto', maxWidth: '480px', padding: '20px 24px', borderRadius: '12px',
              background: isGoldEnvoy ? '#fef9ec' : '#f0fdf4',
              border: `2px solid ${isGoldEnvoy ? '#fbbf24' : '#86efac'}` }}>
              <div style={{ fontWeight: '800', fontSize: '17px', marginBottom: '6px', color: '#1a2332' }}>
                {isGoldEnvoy ? '★ Gold Envoy Membership — $20' : membershipResult?.membership_label || 'Membership confirmed'}
              </div>
              {isGoldEnvoy && (
                <p style={{ fontSize: '14px', color: '#92400e', margin: 0 }}>
                  You are among the first 100 applicants! Your 6-month membership fee is just <strong>$20</strong> (normally $190/$290). 
                  Payment will be requested after your application is approved.
                </p>
              )}
              {!isGoldEnvoy && membershipResult && (
                <p style={{ fontSize: '14px', color: '#166534', margin: 0 }}>
                  Your membership type: <strong>{membershipResult.membership_label}</strong>. 
                  Payment of <strong>${membershipResult.membership_fee}</strong> will be requested after approval.
                </p>
              )}
            </div>

            <button className="btn btn-primary" onClick={() => navigate('/')} data-testid="back-home-btn">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-form-new"><div className="container">
        <div className="form-header">
          <a href="/" className="back-home-link">
            <span className="home-icon">🏠</span> Back to Home
          </a>
          <h1 data-testid="form-title">House Sharing Seniors Application</h1>
          <p className="step-indicator">Step {currentStep} of {totalSteps}</p>
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              data-testid="progress-bar"
            />
          </div>
          {lastSaved && (
            <p className="last-saved">Last saved: {lastSaved.toLocaleTimeString()}</p>
          )}
        </div>

        <div className="form-content">
          {renderStep(currentStep, formData, handleInputChange, errors)}

          {/* Navigation */}
          <div className="form-navigation">
            <div className="nav-left">
              {currentStep > 1 && (
                <button 
                  className="btn btn-secondary" 
                  onClick={handleBack}
                  data-testid="back-btn"
                >
                  ← Back
                </button>
              )}
            </div>
            
            <div className="nav-right">
              <button 
                className="btn btn-secondary save-btn" 
                onClick={saveProgress}
                disabled={isSaving}
                data-testid="save-btn"
              >
                {isSaving ? '💾 Saving...' : '💾 Save Progress'}
              </button>
              
              {currentStep < totalSteps ? (
                <button 
                  className="btn btn-primary" 
                  onClick={handleNext}
                  data-testid="next-btn"
                >
                  Next →
                </button>
              ) : (
                <button 
                  className="btn btn-success" 
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="submit-btn"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Render individual steps
function renderStep(step, formData, handleInputChange, errors) {
  switch (step) {
    case 1: return renderPersonalDetails(formData, handleInputChange, errors);
    case 2: return renderFinancial(formData, handleInputChange, errors);
    case 3: return renderLifestyle(formData, handleInputChange, errors);
    case 4: return renderHousehold(formData, handleInputChange, errors);
    case 5: return renderHealthAccessibility(formData, handleInputChange, errors);
    case 6: return renderSafety(formData, handleInputChange, errors);
    case 7: return renderPreferences(formData, handleInputChange, errors);
    case 8: return renderHouseholdCompatibility(formData, handleInputChange, errors);
    case 9: return renderUsefulItems(formData, handleInputChange, errors);
    case 10: return renderReview(formData);
    default: return null;
  }
}
export { renderStep };

// Step 1: Personal Details — Section A
function renderPersonalDetails(formData, handleInputChange, errors) {
  const isCouple = formData.shared_housing_type === 'couples';
  const INPUT = { className: 'input-field' };
  const GREYED = { className: 'input-field', style: { opacity: 0.4, background: '#f3f4f6' } };

  const partnerCol = (
    <div style={{ flex: 1, opacity: isCouple ? 1 : 0.35, pointerEvents: isCouple ? 'auto' : 'none' }}>
      <div style={{ fontWeight: '800', fontSize: '14px', color: '#2563eb', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {isCouple ? 'Partner 2' : 'Partner 2 (Singles only — greyed out)'}
      </div>
      <div className="form-group">
        <label className="input-label">A2. Given Name {isCouple && '*'}</label>
        <input type="text" name="partner_first_name" value={formData.partner_first_name} onChange={handleInputChange} {...(isCouple ? INPUT : GREYED)} placeholder="Partner's given name" />
      </div>
      <div className="form-group">
        <label className="input-label">A3. Family Name {isCouple && '*'}</label>
        <input type="text" name="partner_last_name" value={formData.partner_last_name} onChange={handleInputChange} {...(isCouple ? INPUT : GREYED)} placeholder="Partner's family name" />
      </div>
      <div className="form-group">
        <label className="input-label">A4. Preferred Name (optional)</label>
        <input type="text" name="partner_preferred_name" value={formData.partner_preferred_name} onChange={handleInputChange} {...(isCouple ? INPUT : GREYED)} placeholder="What they'd like to be called" />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="input-label">A5. Age * (2 digits)</label>
          <input type="number" name="partner_age" value={formData.partner_age} onChange={handleInputChange} {...(isCouple ? INPUT : GREYED)} placeholder="50-99" min="50" max="99" />
        </div>
        <div className="form-group">
          <label className="input-label">A6. Gender</label>
          <select name="partner_gender" value={formData.partner_gender} onChange={handleInputChange} {...(isCouple ? INPUT : GREYED)}>
            <option value="">Select gender</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="non_binary">Non-binary</option>
          </select>
          {errors.partner_gender && <span className="error-message">{errors.partner_gender}</span>}
        </div>
      </div>
    </div>
  );

  return (
    <div className="form-step" data-testid="step-1">
      <h2>Section A — Personal Details</h2>
      <p className="step-description">Please provide your basic information.{isCouple && ' Fill in both partners\' details.'}</p>

      <div className="form-group">
        <label className="input-label">A1. Shared Housing Type Wanted *</label>
        <select name="shared_housing_type" value={formData.shared_housing_type} onChange={handleInputChange} className="input-field" data-testid="housing-type-select">
          <option value="single_female">Single Female — Female Community (4-5 bedroom)</option>
          <option value="single_male">Single Male — Male Community (4-5 bedroom)</option>
          <option value="couples">Couples — Couples (mixed gender) Community (3-4 bedroom)</option>
        </select>
        <p className="field-hint">HSS policy ensures comfort and security - all properties are single-gender households, except for couples.</p>
        {errors.shared_housing_type && <span className="error-message" data-testid="housing-type-error">{errors.shared_housing_type}</span>}
      </div>

      {/* Two-column layout: Person 1 left, Person 2 right */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Person 1 */}
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: '800', fontSize: '14px', color: '#059669', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {isCouple ? 'Partner 1' : 'Your Details'}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">A2. Given Name *</label>
              <input type="text" name="first_name" value={formData.first_name} onChange={handleInputChange} className="input-field" data-testid="first-name-input" />
              {errors.first_name && <span className="error-message">{errors.first_name}</span>}
            </div>
            <div className="form-group">
              <label className="input-label">A3. Family Name *</label>
              <input type="text" name="last_name" value={formData.last_name} onChange={handleInputChange} className="input-field" data-testid="last-name-input" />
              {errors.last_name && <span className="error-message">{errors.last_name}</span>}
            </div>
          </div>
          <div className="form-group">
            <label className="input-label">A4. Preferred Name (optional)</label>
            <input type="text" name="preferred_name" value={formData.preferred_name} onChange={handleInputChange} className="input-field" placeholder="What you'd like to be called" />
            <p className="field-hint">If different from your given name</p>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="input-label">A5. Age * (2 digits only)</label>
              <input type="number" name="age" value={formData.age} onChange={handleInputChange} className="input-field" placeholder="50-99" min="50" max="99" data-testid="dob-input" />
              {errors.age && <span className="error-message">{errors.age}</span>}
            </div>
            <div className="form-group">
              <label className="input-label">A6. Gender</label>
              <select name="gender" value={formData.gender} onChange={handleInputChange} className="input-field" data-testid="gender-select">
                <option value="">Select gender</option>
                <option value="female">Female</option>
                <option value="male">Male</option>
                <option value="non_binary">Non-binary</option>
              </select>
              {errors.gender && <span className="error-message">{errors.gender}</span>}
            </div>
          </div>
        </div>

        {/* Person 2 / Partner */}
        {partnerCol}
      </div>

      <div className="form-row" style={{ marginTop: '16px' }}>
        <div className="form-group">
          <label className="input-label">A7. Phone *</label>
          <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="input-field" placeholder="e.g., 02 1234 5678 or 0412 345 678" data-testid="phone-input" />
          {errors.phone && <span className="error-message">{errors.phone}</span>}
        </div>
        <div className="form-group">
          <label className="input-label">A8. State *</label>
          <select name="state" value={formData.state} onChange={handleInputChange} className="input-field" data-testid="state-select">
            <option value="NSW">NSW</option>
            <option value="VIC">VIC</option>
            <option value="QLD">QLD</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="TAS">TAS</option>
            <option value="NT">NT</option>
            <option value="ACT">ACT</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="input-label">A9. City/Town *</label>
          <input type="text" name="city" value={formData.city} onChange={handleInputChange} className="input-field" placeholder="e.g., Melbourne" data-testid="city-input" />
          {errors.city && <span className="error-message">{errors.city}</span>}
        </div>
        <div className="form-group">
          <label className="input-label">A10. Postcode *</label>
          <input type="text" name="postcode" value={formData.postcode} onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) handleInputChange(e); }} className="input-field" placeholder="e.g., 3000" maxLength={4} data-testid="postcode-input" />
          {errors.postcode && <span className="error-message">{errors.postcode}</span>}
        </div>
      </div>

      <div className="form-group">
        <label className="input-label">A11. Preferred Town/Region</label>
        <input type="text" name="preferred_town_region" value={formData.preferred_town_region} onChange={handleInputChange} className="input-field" placeholder="e.g., Gold Coast, Sunshine Coast, Hunter Valley, Blue Mountains" data-testid="preferred-town-input" />
        <p className="field-hint">Where would you ideally like to live? Enter multiple towns or regions, separated by commas.</p>
      </div>
    </div>
  );
}

export default ApplicationFormNew;
